import { useReducer } from 'react';
import { useEffectOnce } from 'react-use';
import { GeolocationPositionError as GeolocationError } from 'errors';

type GeolocationState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  position: GeolocationPosition | null;
  error: GeolocationError | null;
};

type GeolocationAction =
  | { type: 'error'; error: GeolocationError }
  | { type: 'success'; position: GeolocationPosition }
  | { type: 'loading' };

const geoPositionReducer = (state: GeolocationState, action: GeolocationAction): GeolocationState => {
  switch (action.type) {
    case 'error': {
      return {
        ...state,
        status: 'error',
        error: action.error,
      };
    }

    case 'success': {
      return {
        ...state,
        status: 'success',
        position: action.position,
      };
    }

    case 'loading': {
      return {
        ...state,
        status: 'loading',
      };
    }

    default: {
        throw new Error(`Unhandled action type: ${(action as { type: string }).type}`);
    }
  }
};

const useGeolocation = (_ymaps: unknown, options: PositionOptions = {}) => {
  const [state, dispatch] = useReducer(geoPositionReducer, {
    status: 'idle',
    position: null,
    error: null,
  });
  const { status } = state;

  const handleLocationSuccess = (position: GeolocationPosition) => {
    dispatch({ type: 'success', position });
  };

  const handleLocationFailed = (error: globalThis.GeolocationPositionError) => {
    dispatch({
      type: 'error',
        error: new GeolocationError(error),
    });
  };

  useEffectOnce(() => {
    if (!navigator.geolocation) {
      dispatch({
        type: 'error',
        error: new GeolocationError({ code: 500, message: 'Геолокация на вашем устройстве заблокирована' }),
      });
      return false as unknown as void;
    }

    dispatch({ type: 'loading' });
    navigator.geolocation.getCurrentPosition(handleLocationSuccess, handleLocationFailed, {
      ...options,
      timeout: 5000, // for the case when geolocation disabled in system preferences
      enableHighAccuracy: true,
    });

    const watchId = navigator.geolocation.watchPosition(
      handleLocationSuccess,
      handleLocationFailed,
      {
        ...options,
        enableHighAccuracy: true,
      },      
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  });

  return {
    isLoading: status === 'loading' || status === 'idle',
    isSuccess: status === 'success',
    isIdle: status === 'idle',
    isError: status === 'error',
    ...state,
  };
};

export default useGeolocation;
